import { queryOptions } from '@tanstack/react-query';

import { apiAdminViewsFetch, apiInvestingViewsFetch } from '..';
import type {
  EntityInvestmentBoardDto,
  EntityInvestmentBoardEntryDetailDto,
  MyInvestmentBoardDto,
} from './types';

export const INVESTING_BOARD_QUERY_KEYS = {
  mine: 'investing-board',
  entity: 'investing-board',
} as const;

export const _getMyInvestmentBoard = () =>
  queryOptions({
    queryKey: [INVESTING_BOARD_QUERY_KEYS.mine, 'mine'],
    queryFn: ({ signal }) =>
      apiInvestingViewsFetch<MyInvestmentBoardDto>('/investment-board', { signal }),
  });

/**
 * `entityId` is the caller's active entity (`useSelectedUserConfigSlice().selectedEntityId`),
 * not a request parameter — the endpoint scopes to the session's entity server-side. It is
 * included in the query key only so switching active entity, which does not reload the page,
 * cannot serve one entity's board out of another entity's cache entry.
 */
export const _getEntityInvestmentBoard = (entityId: string | null) =>
  queryOptions({
    queryKey: [INVESTING_BOARD_QUERY_KEYS.entity, 'entity', entityId],
    queryFn: ({ signal }) =>
      apiAdminViewsFetch<EntityInvestmentBoardDto>('/entity-investment-board', { signal }),
  });

/**
 * Nested under the same `[entity, 'entity']` prefix as the board list query so the move
 * mutation's `invalidateQueries({ queryKey: [INVESTING_BOARD_QUERY_KEYS.entity, 'entity'] })`
 * also sweeps a card's open detail — otherwise a move (e.g. confirming funds received) would
 * leave the just-moved card's detail serving its pre-move stage and timeline until `staleTime`
 * elapses.
 */
export const _getEntityInvestmentBoardEntryDetail = (entryId?: string | null) =>
  queryOptions({
    queryKey: [INVESTING_BOARD_QUERY_KEYS.entity, 'entity', 'entry-detail', entryId],
    queryFn: ({ signal }) =>
      apiAdminViewsFetch<EntityInvestmentBoardEntryDetailDto>(
        `/entity-investment-board/entries/${entryId ?? ''}`,
        { signal },
      ),
    enabled: Boolean(entryId),
    staleTime: 30 * 1000,
  });
